// src/lib/readBookingForm.ts
export type BookingPayload = {
  name: string;
  email: string;
  phone: string;
  courtKey: string;
  start: string;        // YYYY-MM-DDTHH:mm (local)
  duration: string;     // minutes as string ("60", "90", ...)
  membership?: string;
  extras?: string;      // JSON string
  totalEUR: string;     // "16" or "16.00"
  player_number: number;
};

const pick = (sel: string) =>
  (document.querySelector<HTMLInputElement | HTMLSelectElement>(sel)?.value ?? "").trim();

export function readBookingPayload(): BookingPayload {
  // Read hidden inputs first
  const name = pick('[name="name"]');
  const email = pick('[name="email"]');
  const phone = pick('[name="phone_hidden"]') || pick('[name="phone"]');
  let courtKey = pick('[name="courtKey"]');
  let start = pick('[name="start"]');
  let duration = pick('[name="duration"]');
  const membership = pick('[name="membership"]');
  const extras = pick('[name="extras"]');
  let totalEUR = pick('[name="totalEUR"]');
  const playerNumberRaw = pick('[name="player_number"]');

  // If any required field missing, try obvious visible selectors as fallback
  if (!courtKey) courtKey = pick('#courtKey') || pick('[data-courtkey]');
  if (!start)    start    = pick('#start') || pick('input[type="datetime-local"]');
  if (!duration) duration = pick('#duration') || pick('[data-duration]');
  if (!totalEUR) totalEUR = pick('#totalEUR') || pick('[data-total]');

  // Normalize slug typo: padel-* -> padel-*
  if (courtKey.startsWith('padel-')) {
    courtKey = courtKey.replace('padel-', 'padel-');
  }

  // Enforce known slugs from Cal
  const ALLOWED = new Set(['padel-1','padel-2','tennis-1','tennis-2','tennis-3']);
  if (!ALLOWED.has(courtKey)) {
    throw new Error(`Ungültiger Platz (courtKey): "${courtKey}". Erlaubt: ${Array.from(ALLOWED).join(', ')}`);
  }

  // Minimal format checks
  const startOk = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(start);
  if (!startOk) throw new Error(`Start muss "YYYY-MM-DDTHH:mm" sein. Aktuell: "${start}"`);
  if (!/^\d+$/.test(duration)) throw new Error(`Dauer (minutes) fehlt/ungültig: "${duration}"`);

  // Price to 2 decimals (string)
  if (totalEUR) {
    const n = Number(totalEUR);
    if (!Number.isFinite(n)) throw new Error(`Preis (totalEUR) ungültig: "${totalEUR}"`);
    totalEUR = n.toFixed(2);
  } else {
    throw new Error('Preis (totalEUR) fehlt.');
  }

  // Mirror values back into hidden inputs (so Make sees them)
  const setHidden = (name: string, v: string) => {
    const el = document.querySelector<HTMLInputElement>(`[name="${name}"]`);
    if (el) el.value = v;
  };
  setHidden('courtKey', courtKey);
  setHidden('start', start);
  setHidden('duration', duration);
  setHidden('totalEUR', totalEUR);

  const player_number = Math.max(1, parseInt(playerNumberRaw, 10) || 2);

  return { name, email, phone, courtKey, start, duration, membership, extras, totalEUR, player_number };
}
